import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { BACKEND_URL } from '../../hooks/useOasisBackend';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { useUser } from '../../hooks/useUser';

interface WebSocketContextType {
  ws: React.MutableRefObject<ReconnectingWebSocket | null>;
  isWsReady: boolean;
  setIsWsReady: (ready: boolean) => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ws = useRef<ReconnectingWebSocket | null>(null);
  const [isWsReady, setIsWsReady] = useState(false);
  const user = useUser();

  useEffect(() => {
    let prefix = 'wss://';
    if (BACKEND_URL.includes('127.0.0.1')) {
      prefix = 'ws://';
    }
    const wsUrl = `${prefix}${BACKEND_URL.replace(/^https?:\/\//, '')}/ws/conversation/`;
    
    // Only create a new connection if one doesn't exist or if it's closed
    if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
      ws.current = new ReconnectingWebSocket(wsUrl);
      
      ws.current.onopen = () => {
        console.log("WebSocket connection established");
        setIsWsReady(true);
      };

      ws.current.onerror = (error) => {
        console.error("WebSocket error:", {
          error: error,
          readyState: ws.current?.readyState,
          url: wsUrl,
          userState: user.state
        });
        setIsWsReady(false);
      };

      ws.current.onclose = (event) => {
        console.log("WebSocket connection closed", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          readyState: ws.current?.readyState,
          url: wsUrl,
          userState: user.state
        });
        setIsWsReady(false);
      };
    }

    return () => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        console.log('WebSocketContext: Cleaning up WebSocket connection');
        ws.current.close();
      }
    };
  }, [user.state]);

  return (
    <WebSocketContext.Provider value={{ ws, isWsReady, setIsWsReady }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}; 